import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { columnsApi } from '@/lib/api';
import type {
  ColumnWithTaskCount,
  CreateColumn,
  UpdateColumn,
  ProjectColumn,
} from 'shared/types';

interface UseColumnMutationsOptions {
  onCreateSuccess?: (column: ProjectColumn) => void;
  onCreateError?: (err: unknown) => void;
  onUpdateSuccess?: (column: ProjectColumn) => void;
  onUpdateError?: (err: unknown) => void;
  onDeleteSuccess?: () => void;
  onDeleteError?: (err: unknown) => void;
  onReorderSuccess?: (columns: ProjectColumn[]) => void;
  onReorderError?: (err: unknown) => void;
}

export function useProjectColumns(projectId: string | null) {
  return useQuery({
    queryKey: ['columns', projectId],
    queryFn: () =>
      projectId ? columnsApi.list(projectId) : Promise.resolve([]),
    enabled: !!projectId,
  });
}

export function useColumnMutations(options?: UseColumnMutationsOptions) {
  const queryClient = useQueryClient();

  const createColumn = useMutation({
    mutationKey: ['createColumn'],
    mutationFn: ({
      projectId,
      data,
    }: {
      projectId: string;
      data: CreateColumn;
    }) => columnsApi.create(projectId, data),
    onSuccess: (column: ProjectColumn, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: ['columns', projectId],
      });
      options?.onCreateSuccess?.(column);
    },
    onError: (err) => {
      console.error('Failed to create column:', err);
      options?.onCreateError?.(err);
    },
  });

  const updateColumn = useMutation({
    mutationKey: ['updateColumn'],
    mutationFn: ({
      columnId,
      data,
    }: {
      columnId: string;
      data: UpdateColumn;
    }) => columnsApi.update(columnId, data),
    onSuccess: (column: ProjectColumn) => {
      // Update column in all relevant caches
      queryClient.setQueryData<ColumnWithTaskCount[]>(
        ['columns', column.project_id],
        (old) => {
          if (!old) return old;
          return old.map((c) => (c.id === column.id ? { ...c, ...column } : c));
        }
      );
      options?.onUpdateSuccess?.(column);
    },
    onError: (err) => {
      console.error('Failed to update column:', err);
      options?.onUpdateError?.(err);
    },
  });

  const deleteColumn = useMutation({
    mutationKey: ['deleteColumn'],
    mutationFn: ({ columnId }: { columnId: string; projectId: string }) =>
      columnsApi.delete(columnId),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({
        queryKey: ['columns', projectId],
      });
      options?.onDeleteSuccess?.();
    },
    onError: (err) => {
      console.error('Failed to delete column:', err);
      options?.onDeleteError?.(err);
    },
  });

  const reorderColumns = useMutation({
    mutationKey: ['reorderColumns'],
    mutationFn: ({
      projectId,
      columnIds,
    }: {
      projectId: string;
      columnIds: string[];
    }) => columnsApi.reorder(projectId, columnIds),
    onSuccess: (columns: ProjectColumn[], { projectId }) => {
      queryClient.setQueryData<ColumnWithTaskCount[]>(
        ['columns', projectId],
        (old) => {
          if (!old) return old;
          return columns.map((col) => {
            const existing = old.find((c) => c.id === col.id);
            return existing
              ? { ...existing, ...col }
              : { ...col, task_count: BigInt(0) };
          });
        }
      );
      options?.onReorderSuccess?.(columns);
    },
    onError: (err) => {
      console.error('Failed to reorder columns:', err);
      options?.onReorderError?.(err);
    },
  });

  return {
    createColumn,
    updateColumn,
    deleteColumn,
    reorderColumns,
  };
}
